import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { api, formatPrice } from "../lib/api";

export default function OrderConfirm() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const trackedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/orders/${id}`);
        setOrder(r.data);
      } catch (_) {
        setOrder(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Fire GA4 `purchase` event once per order (uses sessionStorage to survive remounts)
  useEffect(() => {
    if (!order || trackedRef.current) return;
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const key = `ga4_purchase_${order.id || order.order_number}`;
    try {
      if (window.sessionStorage.getItem(key)) {
        trackedRef.current = true;
        return;
      }
      window.sessionStorage.setItem(key, "1");
    } catch (_) { /* sessionStorage may be unavailable */ }
    trackedRef.current = true;
    window.gtag("event", "purchase", {
      transaction_id: order.order_number,
      value: Number(order.total) || 0,
      currency: "USD",
      items: (order.items || []).map((it) => ({
        item_id: it.product_id,
        item_name: it.name,
        price: Number(it.price) || 0,
        quantity: Number(it.quantity) || 1,
      })),
    });
  }, [order]);

  if (loading) {
    return <div className="pt-40 text-center font-display text-3xl">Confirming…</div>;
  }

  if (!order) {
    return (
      <div className="pt-40 pb-24 text-center" data-testid="order-not-found">
        <p className="font-display text-4xl">Order not found.</p>
        <Link to="/shop" className="btn-primary mt-8 inline-flex">Back to Shop</Link>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24" data-testid="order-confirm-page">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <div className="border border-[var(--mn-ink)] p-10 md:p-16">
          <div className="flex items-center gap-3 text-[var(--mn-accent)]">
            <Check size={20} strokeWidth={1.6} />
            <p className="eyebrow text-[var(--mn-accent)]">Order received</p>
          </div>
          <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95] tracking-tighter">
            Thank you, <em className="font-medium">{order.shipping.full_name.split(" ")[0]}</em>.
          </h1>
          <p className="mt-6 text-[var(--mn-ink-2)] max-w-xl leading-relaxed">
            Your bag is being prepared for shipping. A confirmation has been
            sent to <span className="text-[var(--mn-ink)]">{order.shipping.email}</span>.
          </p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm border-t border-[var(--mn-border)] pt-8">
            <div>
              <p className="eyebrow">Order Nº</p>
              <p className="font-mono-luxe mt-2" data-testid="order-number">{order.order_number}</p>
            </div>
            <div>
              <p className="eyebrow">Date</p>
              <p className="font-mono-luxe mt-2">{new Date(order.created_at).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="eyebrow">Total</p>
              <p className="font-mono-luxe mt-2">{formatPrice(order.total)}</p>
            </div>
            <div>
              <p className="eyebrow">Status</p>
              <p className="font-mono-luxe mt-2 capitalize">{order.status}</p>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-7">
              <p className="eyebrow mb-4">Items</p>
              <ul className="divide-y divide-[var(--mn-border)] border-y border-[var(--mn-border)]">
                {order.items.map((it) => (
                  <li key={it.product_id} className="py-4 flex gap-4 items-center">
                    <div className="w-14 h-16 bg-neutral-100 overflow-hidden">
                      {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-display text-lg">{it.name}</p>
                      <p className="text-xs text-[var(--mn-ink-2)]">Qty {it.quantity}</p>
                    </div>
                    <p className="font-mono-luxe text-sm">{formatPrice(it.price * it.quantity)}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-12 md:col-span-5">
              <p className="eyebrow mb-4">Shipping to</p>
              <address className="not-italic text-sm leading-relaxed text-[var(--mn-ink-2)]" data-testid="order-address">
                {order.shipping.full_name}<br />
                {order.shipping.address_line1}<br />
                {order.shipping.address_line2 && <>{order.shipping.address_line2}<br /></>}
                {order.shipping.city}{order.shipping.state ? `, ${order.shipping.state}` : ""} {order.shipping.postal_code}<br />
                {order.shipping.country}
              </address>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/shop" className="btn-primary" data-testid="order-continue-cta">
              Continue Shopping
            </Link>
            <Link
              to={`/order/track?o=${encodeURIComponent(order.order_number || "")}&e=${encodeURIComponent(order.shipping?.email || "")}`}
              className="btn-ghost"
              data-testid="order-track-cta"
            >
              Track this order
            </Link>
            <Link to="/" className="btn-ghost" data-testid="order-home-cta">
              Return Home
            </Link>
          </div>

          <div className="mt-12 flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[var(--mn-ink-2)]">
            <Sparkles size={14} strokeWidth={1.4} />
            100% Authentic · Money-back guarantee · Tracked worldwide
          </div>
        </div>
      </div>
    </div>
  );
}
